import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Notification from '@veeva/corgix/notification';
import useGetPinnedExtensions from '../../services/data/useGetPinnedExtensions';
import addPinnedExtension from '../../services/addPinnedExtension';
import removePinnedExtension from '../../services/removePinnedExtension';

// Single source of truth for the user's pinned extensions, shared across the whole authenticated app
// (the card/grid list pages AND the nav "Extensions" dropdown). Because every consumer reads the same
// state, an optimistic pin/unpin on one surface is immediately reflected on the others — the dropdown
// no longer shows a stale list after you pin something on the list page.
//
// The default value (empty set + no-op toggle) lets components render outside the provider — e.g. the
// nav in isolated layout tests — without crashing or firing a request.
const PinnedExtensionsContext = createContext({ pinnedSet: new Set(), togglePin: () => {} });

export function PinnedExtensionsProvider({ children }) {
    // Fetched once for the session (the provider mounts with the authenticated container). A failed
    // fetch is treated as "no pins" so the app still renders.
    const [pinned] = useGetPinnedExtensions();
    const [pinnedSet, setPinnedSet] = useState(() => new Set());

    useEffect(() => {
        if (pinned) {
            setPinnedSet(new Set(pinned));
        }
    }, [pinned]);

    const togglePin = useCallback(
        (urlName) => {
            // Decide the direction from the current set (this render's value) — not from inside the
            // setState updater, which React may run later, leaving the direction undefined here.
            const didPin = !pinnedSet.has(urlName);
            setPinnedSet((prev) => {
                const next = new Set(prev);
                if (didPin) {
                    next.add(urlName);
                } else {
                    next.delete(urlName);
                }
                return next;
            });

            const request = didPin ? addPinnedExtension : removePinnedExtension;
            request(urlName).catch(() => {
                // Revert the optimistic change on failure.
                setPinnedSet((prev) => {
                    const next = new Set(prev);
                    if (didPin) {
                        next.delete(urlName);
                    } else {
                        next.add(urlName);
                    }
                    return next;
                });
                Notification.error(
                    didPin ? 'Failed to pin extension.' : 'Failed to unpin extension.',
                );
            });
        },
        [pinnedSet],
    );

    const value = useMemo(() => ({ pinnedSet, togglePin }), [pinnedSet, togglePin]);

    return (
        <PinnedExtensionsContext.Provider value={value}>
            {children}
        </PinnedExtensionsContext.Provider>
    );
}

PinnedExtensionsProvider.propTypes = {
    children: PropTypes.node,
};

export default function usePinnedExtensions() {
    return useContext(PinnedExtensionsContext);
}
